import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, Heart, Shield, Users, ArrowRight, MapPin, Phone, Sparkles, Play, CheckCircle, MessageCircle, GraduationCap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floating decorative elements component
const FloatingElements = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-20 left-10 w-16 h-16 bg-yellow-300/30 rounded-full animate-float" style={{ animationDelay: '0s' }} />
    <div className="absolute top-40 right-20 w-12 h-12 bg-emerald-300/30 rounded-full animate-float" style={{ animationDelay: '1s' }} />
    <div className="absolute bottom-40 left-1/4 w-20 h-20 bg-orange-300/20 rounded-full animate-float" style={{ animationDelay: '2s' }} />
    <div className="absolute top-1/3 right-1/4 w-8 h-8 bg-pink-300/30 rounded-full animate-float" style={{ animationDelay: '0.5s' }} />
    <div className="absolute bottom-20 right-10 w-14 h-14 bg-yellow-400/20 rounded-full animate-float" style={{ animationDelay: '1.5s' }} />
  </div>
);

// Review Form Modal Component
const ReviewFormModal = ({ isOpen, onClose, onSubmit }) => {
  const [firstName, setFirstName] = useState("");
  const [lastInitial, setLastInitial] = useState("");
  const [content, setContent] = useState("");
  const [rating, setRating] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastInitial.trim() || !content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (lastInitial.length > 1) {
      toast.error("Please enter only the first letter of your last name");
      return;
    }
    
    const formattedName = `${firstName.trim()} ${lastInitial.toUpperCase()}.`;
    
    setIsSubmitting(true);
    await onSubmit({
      parent_name: formattedName,
      content: content.trim(),
      rating
    });
    setIsSubmitting(false);
    setFirstName("");
    setLastInitial("");
    setContent("");
    setRating(5);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg rounded-3xl border-2 border-yellow-200 animate-scale-in">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-yellow-500 animate-wiggle" />
            Share Your Experience
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="firstName" className="text-slate-700 font-medium">
                First Name *
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Sarah"
                className="rounded-xl border-orange-200 focus:border-yellow-400 h-12 transition-all duration-300 focus:scale-[1.02]"
                data-testid="review-first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastInitial" className="text-slate-700 font-medium">
                Last Initial *
              </Label>
              <Input
                id="lastInitial"
                value={lastInitial}
                onChange={(e) => setLastInitial(e.target.value.slice(0, 1))}
                placeholder="M"
                maxLength={1}
                className="rounded-xl border-orange-200 focus:border-yellow-400 h-12 transition-all duration-300 focus:scale-[1.02] text-center uppercase"
                data-testid="review-last-initial-input"
              />
            </div>
          </div>
          
          <p className="text-sm text-slate-500 -mt-4">
            Your name will appear as: <span className="font-semibold text-slate-700">{firstName || "First"} {lastInitial?.toUpperCase() || "L"}.</span>
          </p>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Rating *</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="p-1 transition-all duration-200 hover:scale-125"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 transition-all duration-200 ${
                      star <= (hoveredStar || rating)
                        ? "text-yellow-400 fill-yellow-400 drop-shadow-lg"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="content" className="text-slate-700 font-medium">
              Your Review *
            </Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Tell us about your experience with Good Hands Family Daycare..."
              className="rounded-xl border-orange-200 focus:border-yellow-400 min-h-[120px] transition-all duration-300 focus:scale-[1.01]"
              data-testid="review-content-input"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full font-bold hover:bg-gray-100 transition-all duration-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 btn-playful"
              data-testid="submit-review-btn"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" /> Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Heart className="w-5 h-5" /> Submit Review
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Comment Form Modal Component
const CommentFormModal = ({ isOpen, onClose, onSubmit, testimonialName }) => {
  const [firstName, setFirstName] = useState("");
  const [lastInitial, setLastInitial] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastInitial.trim() || !comment.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (lastInitial.length > 1) {
      toast.error("Please enter only the first letter of your last name");
      return;
    }
    
    const formattedName = `${firstName.trim()} ${lastInitial.toUpperCase()}.`;
    
    setIsSubmitting(true);
    await onSubmit({
      commenter_name: formattedName,
      comment: comment.trim()
    });
    setIsSubmitting(false);
    setFirstName("");
    setLastInitial("");
    setComment("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg rounded-3xl border-2 border-yellow-200 animate-scale-in">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-yellow-500" />
            Reply to {testimonialName}'s Review
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="commenterFirstName" className="text-slate-700 font-medium">
                First Name *
              </Label>
              <Input
                id="commenterFirstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className="rounded-xl border-orange-200 focus:border-yellow-400 h-11"
                data-testid="comment-first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="commenterLastInitial" className="text-slate-700 font-medium">
                Last Initial *
              </Label>
              <Input
                id="commenterLastInitial"
                value={lastInitial}
                onChange={(e) => setLastInitial(e.target.value.slice(0, 1))}
                placeholder="D"
                maxLength={1}
                className="rounded-xl border-orange-200 focus:border-yellow-400 h-11 text-center uppercase"
                data-testid="comment-last-initial-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment" className="text-slate-700 font-medium">
              Your Comment *
            </Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your reply..."
              className="rounded-xl border-orange-200 focus:border-yellow-400 min-h-[100px]"
              data-testid="comment-content-input"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 btn-playful"
              data-testid="submit-comment-btn"
            >
              {isSubmitting ? "Posting..." : "Post Comment"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
};

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    fetchData();
    
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const fetchData = async () => {
    try {
      const [testimonialsRes, programsRes] = await Promise.all([
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/programs`)
      ]);
      setTestimonials(testimonialsRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      await axios.post(`${API}/testimonials`, {
        ...reviewData,
        child_program: "Toddler Program"
      });
      toast.success("Thank you for your review! It will appear after approval.", {
        icon: "🎉",
      });
      setShowReviewForm(false);
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review. Please try again.");
    }
  };

  const handleCommentSubmit = async (commentData) => {
    if (!selectedTestimonial) return;
    try {
      await axios.post(`${API}/testimonials/${selectedTestimonial.id}/comments`, commentData);
      toast.success("Comment posted successfully!", {
        icon: "💬",
      });
      setShowCommentForm(false);
      setSelectedTestimonial(null);
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error posting comment:", error);
      toast.error("Failed to post comment. Please try again.");
    }
  };

  const openCommentForm = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setShowCommentForm(true);
  };

  const features = [
    { icon: Heart, title: "Loving Care", description: "Every child receives personalized attention and genuine affection", color: "bg-pink-100 text-pink-600" },
    { icon: Shield, title: "Safe Environment", description: "State-licensed facility with comprehensive safety measures", color: "bg-emerald-100 text-emerald-600" },
    { icon: Users, title: "Family Atmosphere", description: "Small group sizes ensure your child feels at home", color: "bg-blue-100 text-blue-600" },
  ];

  const programIcons = {
    toddler: Sparkles,
    preschool: GraduationCap,
  };

  const programColors = {
    toddler: "bg-emerald-50 border-emerald-200",
    preschool: "bg-sky-50 border-sky-200",
  };

  return (
    <div className="noise-bg" data-testid="home-page">
      <FloatingElements />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Decorative Blobs */}
        <div className="hero-blob absolute -top-32 -right-32" />
        <div className="hero-blob absolute top-1/2 -left-40 animation-delay-2000" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-slide-in-left">
              <span className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-bounce-slow">
                <Sparkles className="w-4 h-4" />
                Licensed Family Daycare in Warrenton
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Where Little Ones{" "}
                <span className="gradient-text">Grow & Thrive</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg animate-fade-in-delay-1">
                At Good Hands Family Daycare, we provide a nurturing home environment where your child can learn, play, and develop with loving care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-delay-2">
                <Link to="/book-visit">
                  <Button 
                    className="btn-playful text-base group"
                    data-testid="hero-book-tour-btn"
                  >
                    Book a Tour
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button 
                    variant="outline"
                    className="rounded-full px-8 py-4 text-base font-bold border-2 border-yellow-400 text-yellow-600 hover:bg-yellow-50 hover:scale-105 transition-all duration-300"
                    data-testid="hero-view-programs-btn"
                  >
                    <Play className="mr-2 w-5 h-5" />
                    View Programs
                  </Button>
                </Link>
              </div>
              
              {/* Stats */}
              <div className="mt-10 flex gap-8 animate-fade-in-delay-3">
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-600">
                    <AnimatedCounter end={10} suffix="+" />
                  </p>
                  <p className="text-sm text-slate-600">Years Experience</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">
                    <AnimatedCounter end={100} suffix="+" />
                  </p>
                  <p className="text-sm text-slate-600">Happy Families</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-orange-600">
                    <AnimatedCounter end={5} />
                  </p>
                  <p className="text-sm text-slate-600">Star Rating</p>
                </div>
              </div>
              
              {/* Location Badge */}
              <div className="mt-8 flex items-center gap-2 text-slate-600 animate-fade-in-delay-4">
                <MapPin className="w-5 h-5 text-yellow-500 animate-bounce-slow" />
                <span className="text-sm">2689 Travers Place, Warrenton, VA 20187</span>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-slide-in-right">
              <div className="relative z-10 group">
                <img
                  src="https://images.unsplash.com/photo-1565425518476-3229123699c5?w=800&q=80"
                  alt="Black and white kids playing together"
                  className="rounded-3xl shadow-2xl shadow-yellow-500/20 w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  data-testid="hero-image"
                />
                {/* Overlay badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl animate-bounce-slow">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Heart className="w-5 h-5 text-white" fill="white" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">Trusted Care</p>
                      <p className="text-xs text-slate-500">Since 2016</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-emerald-300 rounded-2xl -z-10 animate-float" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-yellow-300 rounded-full -z-10 animate-float animation-delay-2000" />
              <div className="absolute top-1/2 -right-10 w-16 h-16 bg-orange-300/50 rounded-xl -z-10 animate-float animation-delay-4000" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" data-animate className="py-20 bg-white relative overflow-hidden" data-testid="features-section">
        <div className="absolute inset-0 dots-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Families <span className="gradient-text">Choose Us</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We're committed to providing the best early childhood experience for your little ones.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 stagger-children">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border border-orange-100 shadow-xl shadow-orange-500/5 rounded-3xl card-hover group cursor-pointer"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className={`w-20 h-20 ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <feature.icon className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-yellow-600 transition-colors">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-20" data-testid="programs-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our <span className="gradient-text">Toddler & Preschool Programs</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Nurturing programs that help curious toddlers explore and preschoolers get ready for kindergarten.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto stagger-children">
            {programs.map((program) => {
              const Icon = programIcons[program.id] || Sparkles;
              const colorClass = programColors[program.id] || "bg-emerald-50 border-emerald-200";
              
              return (
                <Card 
                  key={program.id}
                  className={`${colorClass} border-2 rounded-3xl card-hover overflow-hidden group`}
                  data-testid={`program-preview-${program.id}`}
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <Icon className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{program.name}</h3>
                    <p className="text-slate-600 text-sm mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      {program.age_range}
                    </p>
                    <p className="text-slate-700 mb-6">{program.description}</p>
                    
                    {/* Features list */}
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {program.features?.slice(0, 4).map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link to="/programs">
                      <Button 
                        variant="ghost" 
                        className="rounded-full text-slate-900 hover:bg-white/50 p-0 font-semibold group/btn"
                      >
                        Learn More 
                        <ArrowRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs">
              <Button 
                className="btn-playful group"
                data-testid="view-all-programs-btn"
              >
                View Program Details
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About/Image Section */}
      <section className="py-20 bg-white overflow-hidden" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative group">
                <img
                  src="https://images.unsplash.com/photo-1575650980117-a9383476a9b4?w=700&q=80"
                  alt="Children playing with toys together"
                  className="rounded-3xl shadow-xl w-full transition-transform duration-500 group-hover:scale-[1.02]"
                  data-testid="about-image"
                />
                <div className="absolute -bottom-8 -right-8 bg-yellow-400 rounded-2xl p-6 shadow-lg animate-bounce-slow">
                  <p className="text-4xl font-bold text-slate-900">10+</p>
                  <p className="text-sm text-slate-700">Years Experience</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                About Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                A Home <span className="gradient-text">Away From Home</span>
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Good Hands Family Daycare offers a warm, nurturing environment where children can explore, learn, and grow. Our home-based setting provides the intimate attention that larger centers often can't match.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                With small group sizes and dedicated caregivers, we ensure each child receives the personalized care they deserve. From creative play to early learning activities, we prepare your child for a bright future.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/book-visit">
                  <Button 
                    className="btn-playful group"
                    data-testid="about-schedule-visit-btn"
                  >
                    Schedule a Visit
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="tel:+15715359999" className="flex items-center gap-2 text-slate-700 hover:text-yellow-600 transition-colors group">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                    <Phone className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="font-medium">(571) 535-9999</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Parents <span className="gradient-text">Say</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it – hear from the families we serve.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 stagger-children">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card 
                key={testimonial.id || index}
                className="border border-orange-100 shadow-xl shadow-orange-500/5 rounded-3xl card-hover group"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-8">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i}
                        className={`w-5 h-5 transition-transform duration-300 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400 group-hover:scale-110' : 'text-gray-200'}`}
                        style={{ transitionDelay: `${i * 50}ms` }}
                      />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-lg font-bold text-white">
                          {testimonial.parent_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{testimonial.parent_name}</p>
                        {testimonial.child_program && (
                          <p className="text-sm text-slate-500">{testimonial.child_program}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => openCommentForm(testimonial)}
                      className="p-2 rounded-full hover:bg-yellow-100 transition-colors group/btn"
                      title="Reply to review"
                      data-testid={`reply-btn-${index}`}
                    >
                      <MessageCircle className="w-5 h-5 text-slate-400 group-hover/btn:text-yellow-600" />
                    </button>
                  </div>
                  
                  {/* Comments Section */}
                  {testimonial.comments && testimonial.comments.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-orange-100 space-y-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {testimonial.comments.length} {testimonial.comments.length === 1 ? 'Reply' : 'Replies'}
                      </p>
                      {testimonial.comments.map((comment, cIndex) => (
                        <div 
                          key={comment.id || cIndex} 
                          className="bg-yellow-50 rounded-xl p-3 animate-fade-in"
                        >
                          <p className="text-sm text-slate-700">{comment.comment}</p>
                          <p className="text-xs text-slate-500 mt-2">— {comment.commenter_name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add Review Button */}
          <div className="text-center mt-12">
            <Button 
              onClick={() => setShowReviewForm(true)}
              variant="outline"
              className="rounded-full px-8 py-4 font-bold border-2 border-yellow-400 text-yellow-600 hover:bg-yellow-50 hover:scale-105 transition-all duration-300 group"
              data-testid="add-review-btn"
            >
              <Star className="mr-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
              Share Your Experience
            </Button>
          </div>
        </div>
      </section>

      {/* Review Submission Modal */}
      <ReviewFormModal 
        isOpen={showReviewForm} 
        onClose={() => setShowReviewForm(false)}
        onSubmit={handleReviewSubmit}
      />

      {/* Comment Modal */}
      <CommentFormModal
        isOpen={showCommentForm}
        onClose={() => {
          setShowCommentForm(false);
          setSelectedTestimonial(null);
        }}
        onSubmit={handleCommentSubmit}
        testimonialName={selectedTestimonial?.parent_name || ""}
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-400 via-yellow-400 to-orange-400 relative overflow-hidden" data-testid="cta-section">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-float" />
          <div className="absolute bottom-10 right-10 w-24 h-24 bg-white/10 rounded-full animate-float animation-delay-2000" />
          <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full animate-float animation-delay-4000" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to Join Our Family?
          </h2>
          <p className="text-slate-800 text-lg mb-8 max-w-2xl mx-auto">
            Schedule a tour today and see why families trust Good Hands for their childcare needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-visit">
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-4 text-base font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                data-testid="cta-book-tour-btn"
              >
                Book a Tour Today
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="tel:+15715359999">
              <Button 
                variant="outline"
                className="bg-white hover:bg-white/90 text-slate-900 border-2 border-slate-900 rounded-full px-8 py-4 text-base font-bold hover:scale-105 transition-all duration-300 group"
                data-testid="cta-call-btn"
              >
                <Phone className="mr-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
                Call Us: (571) 535-9999
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
